import {inject, Injectable, InjectionToken, OnDestroy} from "@angular/core";
import {debounceTime, ReplaySubject, Subject, Subscription} from "rxjs";
import {ControllerEvent} from "./ControllerEvent";

export const IT_CONTROLLER_EVENT_BUS_CONFIGS = new InjectionToken<any>('IT_CONTROLLER_EVENT_BUS_CONFIGS');
export const IT_CONTROLLER_EVENT_BUS_NAME = new InjectionToken<string>('IT_CONTROLLER_EVENT_BUS_NAME');

@Injectable()
export class ControllerEventBus implements OnDestroy {

  private eventBus$: Map<string, Subject<ControllerEvent>>;
  private readonly configs: any = null;
  private readonly eventBusName: string;

  constructor() {
    this.configs = inject(IT_CONTROLLER_EVENT_BUS_CONFIGS, {optional: true});
    this.eventBus$ = new Map();

    this.eventBusName = this.getDefaultName();

    if(this.eventBusName.length === 0) {
      this.eventBusName = inject(IT_CONTROLLER_EVENT_BUS_NAME, {optional: true}) || '';
    }

    if(this.eventBusName.length !== 0) {
      console.log(`Controller event bus (${this.eventBusName}) created`);
    }
    else {
      console.log(`Controller event bus created`);
    }
  }

  protected getDefaultName(): string {
    return '';
  }

  protected getOrCreateTopic(topicName: string): Subject<ControllerEvent> {
    if(this.eventBus$.has(topicName)) {
      return this.eventBus$.get(topicName)!;
    }
    else {
      console.log(`(${this.eventBusName}) Creating controller event topic: ${topicName}`);

      if(!this.configs) {
        const subject = new ReplaySubject<ControllerEvent>(100, 3000);
        this.eventBus$.set(topicName, subject);

        return subject;
      }
      else {
        const bufferSize = this.configs['bufferSize'] || 100;
        const windowTime = this.configs['windowTime'] || 3000;

        const subject = new ReplaySubject<ControllerEvent>(bufferSize, windowTime);
        this.eventBus$.set(topicName, subject);

        return subject;
      }
    }
  }

  dispatch(event: ControllerEvent): void {
    console.debug(`(${this.eventBusName}) Dispatching controller event: ${event.eventName}`);
    this.getOrCreateTopic(event.eventName).next(event);
  }

  subscribe(eventName: string,
            handler: (event: ControllerEvent) => void,
            silent: boolean = false): Subscription {
    return this.getOrCreateTopic(eventName).subscribe((event) => {
      this.internalHandle(eventName, event, handler, null, silent);
    });
  }

  subscribeWithFilter(eventName: string,
                      handler: (event: ControllerEvent) => void,
                      filter: (event: ControllerEvent) => boolean,
                      silent: boolean = false): Subscription {
    return this.getOrCreateTopic(eventName).subscribe((event) => {
      this.internalHandle(eventName, event, handler, filter, silent);
    });
  }

  subscribeWithDebounce(eventName: string,
                        time: number,
                        handler: (event: ControllerEvent) => void,
                        silent: boolean = false): Subscription {
    return this.getOrCreateTopic(eventName).pipe(debounceTime(time)).subscribe((event) => {
      this.internalHandle(eventName, event, handler, null, silent);
    });
  }

  subscribeWithDebounceAndFilter(eventName: string,
                                 time: number,
                                 handler: (event: ControllerEvent) => void,
                                 filter: (event: ControllerEvent) => boolean,
                                 silent: boolean = false): Subscription {
    return this.getOrCreateTopic(eventName).pipe(debounceTime(time)).subscribe((event) => {
      this.internalHandle(eventName, event, handler, filter, silent);
    });
  }

  private internalHandle(eventName: string,
                         event: ControllerEvent,
                         handler: (event: ControllerEvent) => void,
                         filter: ((event: ControllerEvent) => boolean) | null = null,
                         silent: boolean = false) {
    if(event.eventName === eventName) {
      try {
        let match = true;

        if(filter) {
          match = filter(event);
        }

        if(match) {
          if(!silent) {
            console.debug(`(${this.eventBusName}) Handling controller event: ${event.eventName}`);
          }

          handler(event);
        }
      }
      catch(error) {
        console.error(`(${this.eventBusName}) Failed to handle controller event: ${event.eventName}`, error);
      }
    }
  }

  ngOnDestroy() {
    console.log(`(${this.eventBusName}) Destroying controller event bus`);

    this.eventBus$.forEach((subject, topic) => {
      subject.complete();
    });

    this.eventBus$.clear();
  }
}
